import {
  createEffect,
  createSignal,
  onCleanup,
  onMount,
  Show,
  Switch,
  Match,
  For,
} from 'solid-js';
import { eventTrackerStore } from '@/utils/event-tracker-store';
import { isMobile, setIsMobile } from '@/utils/isMobileSignal';
import {
  SearchResultCards,
  SearchResultCard,
} from '@/components/SearchResultCard';
import { SearchIcon } from './Icons';
import { AnswerCard } from './AnswerCard';
import LoadingIndicator from './LoadingIndicator';
import {
  EventType,
  Feedback,
  PlatformIdentifyResponse,
  SearchIntent,
  VERSION,
  WIDGET_SOURCE,
  WIDGET_TYPE,
} from '@/types/constants';
import { streamAnswer } from '@/utils/stream';
import { SearchProps, SearchResult } from '@/types/search';
import { NextBestActions } from './AnswerCard/NextBestActions';
import { Endpoints } from '@/endpoints';
import { kyPost } from '@/utils/post-request';
import { ITrackPropsData } from '@/utils/trackEvent';
import { isMissingResponse } from '@/utils/common';
import { DEFAULT_NO_ANSWER_RESPONSE, WidgetMode } from '@/constants';
import { AutocompleteSearchInput } from '@brainfish-ai/solid-components';
import styles from '@brainfish-ai/solid-components/styles?inline';
import { getUtmParameters } from '@/utils/utm';

import { Logger } from '../utils/logger';
import classNames from 'classnames';

export const Search = (props: SearchProps) => {
  let searchContainer: HTMLDivElement | undefined;
  const [eventTracker] = eventTrackerStore;
  const [searching, setSearching] = createSignal(false);
  const [searchQuery, setSearchQuery] = createSignal('');
  const [searchResults, setSearchResults] = createSignal([] as SearchResult[]);
  const [collections, setCollections] = createSignal([]);
  const [answer, setAnswer] = createSignal('');
  const [answering, setAnswering] = createSignal(false);
  const [identifyingIntent, setIdentifyingIntent] = createSignal(false);
  const [searchIntent, setSearchIntent] = createSignal<
    SearchIntent | undefined
  >();
  const [clearSearch, setClearSearch] = createSignal(true);
  const [_searchCountDown, setSearchCountDown] = createSignal(0);
  const [_loadingMessage, setLoadingMessage] = createSignal('');
  const [noResults, setNoResults] = createSignal(false);
  const apiHost = props.apiHost || 'https://app.brainfi.sh';
  const [promptId, setPromptId] = createSignal('');
  const [helpCenterUrl, setHelpCenterUrl] = createSignal('');
  const [searchQueryId, setSearchQueryId] = createSignal('');
  const searchIntentId = props.searchIntentId || '';
  const suggestions = props.settings?.suggestions || [];
  const [initialized, setInitialized] = createSignal(false);
  const isChromeExtension = props.isChromeExtension || false;

  const answerNotFoundMessage = props.settings?.answerNotFoundMessage || '';

  // --- display controls
  const [showSearch, setShowSearch] = createSignal(false);
  const [showSuggestions, setShowSuggestions] = createSignal(false);
  const [displayCategoriesSelection, setDisplayCategoriesSelection] =
    createSignal(false);
  const [showSearching, setShowSearching] = createSignal(false);
  const [showAnswering, setShowAnswering] = createSignal(false);
  const [showAnswer, setShowAnswer] = createSignal(false);
  const [showResultCards, setShowResultCards] = createSignal(false);
  const [showNoResults, setShowNoResults] = createSignal(false);
  const [
    showNextBestActionsWithoutAnswer,
    setShowNextBestActionsWithoutAnswer,
  ] = createSignal(false);

  // destructure props.settings if props.settings is defined
  const {
    hideFeedback,
    showDisclaimer,
    disclaimer,
    placeholder,
    limit,
    nextBestActions,
    hideAnswers,
    loadingSearchText,
    loadingAnswerText,
    generatingAnswerText,
    suggestedAnswerText,
  } = props.settings || {};

  const isPlatform =
    isChromeExtension || props.widgetSource === WIDGET_SOURCE.PLATFORM;
  const isTestIntent = props.widgetSource === WIDGET_SOURCE.TEST_INTENT;
  const isSidebar =
    props.widgetType === WIDGET_TYPE.HELP ||
    props.widgetMode === WidgetMode.SLIDE_OVER;
  // Intent and actions are not enabled on platform except for the test intent screen
  const isIntentAndActionsEnabled = !isPlatform || isTestIntent;

  // Define a mapping of conditions to EndpointSource values
  const endpointSourceMap = [
    { condition: isPlatform, value: 'Platform' },
    { condition: isTestIntent, value: 'TestIntent' },
    { condition: true, value: 'Widget' }, // Default case
  ] satisfies {
    condition: boolean;
    value: 'Platform' | 'TestIntent' | 'Widget';
  }[];

  // Find the first matching condition and use its value
  const EndpointSource = endpointSourceMap.find(
    (item) => item.condition,
  )!.value;

  createEffect(() => {
    const isSearchNotCleared = !clearSearch();
    const hasSuggestions = suggestions.length > 0;
    const hasSearchResults = searchResults().length > 0;
    const hasAnswer = !!answer();
    const isSearching = searching();
    const hasNoResults = noResults();

    const showSearch =
      clearSearch() ||
      isSearching ||
      hasSuggestions ||
      hasAnswer ||
      hasSearchResults ||
      hasNoResults;
    setShowSearch(showSearch);

    if (isSearchNotCleared) {
      const showSearching = isSearching;
      setShowSearching(showSearching);

      const showAnswering = answering() && !hasAnswer;
      setShowAnswering(showAnswering);

      const showResultCards =
        hasSearchResults &&
        !isSearching &&
        searchResults().slice(0, limit).length > 0;
      setShowResultCards(showResultCards);

      const showNoResults = hasNoResults;
      setShowNoResults(showNoResults);

      const showNextBestActionsWithoutAnswer =
        !!hideAnswers &&
        !!nextBestActions &&
        nextBestActions.length > 0 &&
        (showResultCards || showNoResults);
      setShowNextBestActionsWithoutAnswer(showNextBestActionsWithoutAnswer);
      const showAnswer = !showAnswering && !hideAnswers && hasAnswer;
      setShowAnswer(showAnswer);
    }
    const showSuggestions =
      initialized() &&
      !isSearchNotCleared &&
      !hasSearchResults &&
      !isSearching &&
      hasSuggestions;
    setShowSuggestions(showSuggestions);
  });

  const resizeObserver = new ResizeObserver((entries) => {
    return setIsMobile(entries[0].target.clientWidth < 400);
  });

  const startTimer = (): any => {
    const timer = setInterval(() => {
      setSearchCountDown((countDown) => countDown + 1);
    }, 1000);

    return timer;
  };

  const stopTimer = (timer: number) => {
    clearInterval(timer);
    setSearchCountDown(0);
  };

  const handleSearch = (query: string) => {
    if (!query || !initialized()) return;
    if (props.settings?.searchByCategory) {
      setDisplayCategoriesSelection(true);

      return;
    } else {
      setSearchResults([]);
      setAnswer('');
      setSearchIntent(undefined);

      searchArticles(query);
    }
  };

  const searchArticles = async (
    query: string,
    selectedCollectionId?: string,
  ) => {
    const allowedRegions = props.settings?.regions;
    const searchScope = props.settings?.searchScope || 'all';
    const searchType = props.settings?.searchType || 'smart';
    setSearching(true);
    setAnswering(false);
    setAnswer('');
    setNoResults(false);
    setSearchResults([]);

    const timer = startTimer();

    const collectionId = selectedCollectionId || props.settings?.collectionId;

    try {
      eventTracker?.track({
        name: 'Input Field Search',
        type: EventType.InputFieldSearch,
        data: {
          searchQuery: query,
          searchQueryId: searchQueryId(),
          promptId: promptId(),
          answer: answer(),
        },
      });
      let searchUrlPath: string = Endpoints[EndpointSource].DocumentsSearch;
      const identifyIntentPath = Endpoints[EndpointSource].IdentifyIntent;

      if (
        isPlatform &&
        EndpointSource === 'Platform' &&
        searchType === 'keyword'
      ) {
        searchUrlPath = Endpoints[EndpointSource].KeywordSearch;
      }

      const fetchArticles = async () => {
        let additionalProps = {};

        if (isPlatform) {
          additionalProps = {
            source: isChromeExtension ? 'agent_assist' : 'app',
            onlyPublic: searchScope === 'public',
            isKeywordSearch: searchType === 'keyword',
          };
        } else if (isTestIntent) {
          additionalProps = { source: 'test_action' };
        }

        try {
          const response = await kyPost<any>({
            endpoint: `${apiHost}/api/${searchUrlPath}`,
            apiKey: props.apiKey,
            requestBody: {
              query,
              collectionId,
              allowedRegions,
              limit: limit || 5,
              ...additionalProps,
            },
          });
          setSearchQueryId(response?.searchQuery?.id);

          let articles = await response.data;

          // Transform keyword search response to match expected format
          if (isPlatform && searchType === 'keyword' && articles) {
            articles = (Array.isArray(articles) ? articles : []).map(
              (article: any) => ({
                id: article.id,
                title: article.title,
                chunk: article.chunk || article.sanitizedContext || '',
                collection: collections().find(
                  (collection: any) => collection.id === article.collectionId,
                ) || { name: '' },
                isPublic: article.document?.siteEnabled ?? true,
                url: article.url,
                syncSource: article.syncSource,
              }),
            );
          }

          if (response.baseUrl) {
            setHelpCenterUrl(response.baseUrl);
          } else if (isPlatform) {
            setHelpCenterUrl(
              window.location.host.includes('localhost')
                ? 'http://localhost:3000'
                : 'https://app.brainfi.sh',
            );
          }
          if (articles?.length === 0) {
            setNoResults(true);
            eventTracker?.track({
              name: 'No Articles Found',
              type: EventType.NoArticlesFound,
              data: {
                searchQuery: query,
                searchQueryId: searchQueryId(),
                promptId: promptId(),
                answer: answer(),
              },
            });
          } else {
            // if no results, show no results message
            if (articles.length === 0) {
              setNoResults(true);
            } else {
              const context = {
                articles,
                searchQueryId: response?.searchQuery?.id,
              };
              setSearchResults(articles);
              // if search type is keyword, do not fetch answer
              if (searchType === 'smart') {
                if (isIntentAndActionsEnabled) {
                  identifyIntent();
                }
                fetchAnswer(query, context);
              }
            }
          }
        } catch (error) {
          Logger.error('Error processing articles response:', error as Error, {
            function: 'fetchArticles',
            filename: 'SearchWidget.tsx',
            widgetKey: props.apiKey,
            version: props.version,
          });
          // Handle error in processing fetchArticles response
        } finally {
          setSearching(false);
          stopTimer(timer);
          setSearchCountDown(0);
          setLoadingMessage('');
        }
      };

      const identifyIntent = async () => {
        try {
          setIdentifyingIntent(true);
          const response = await kyPost<{
            data: PlatformIdentifyResponse;
          }>({
            endpoint: `${apiHost}/api/${identifyIntentPath}`,
            accessToken: props.accessToken,
            apiKey: props.apiKey,
            requestBody: {
              searchQueryId: searchQueryId(),
              ...(!!searchIntentId && { searchIntentId }),
            },
          });
          const searchIntent = response.data;
          if (searchIntent.searchIntentIdentified) {
            setSearchIntent(searchIntent.searchIntent);
            const trackData = {
              data: {
                searchQuery: query,
                searchQueryId: searchQueryId(),
                promptId: promptId(),
                answer: answer(),
                searchIntent: searchIntent.searchIntent,
              } satisfies ITrackPropsData,
            };
            eventTracker?.track({
              ...trackData,
              name: 'Intent Identified',
              type: EventType.IntentIdentified,
            });
          } else {
            setSearchIntent(undefined);
          }
        } catch (error) {
          Logger.error('Error identifying intent: ', error as Error, {
            function: 'identifyIntent',
            filename: 'SearchWidget.tsx',
            widgetKey: props.apiKey,
            version: props.version,
          });
        } finally {
          setIdentifyingIntent(false);
        }
      };

      fetchArticles();
    } catch (error) {
      setSearching(false);
      stopTimer(timer);
      setSearchCountDown(0);
      setLoadingMessage('');
      Logger.error('Error searching articles', error as Error, {
        function: 'searchArticles',
        filename: 'SearchWidget.tsx',
        widgetKey: props.apiKey,
        version: props.version,
      });
    }
  };

  const fetchCollections = async () => {
    const allowedRegions = props.settings?.regions;

    try {
      const collectionListUrlPath = Endpoints[EndpointSource].CollectionList;

      const response = await kyPost<any>({
        endpoint: `${apiHost}/api/${collectionListUrlPath}`,
        apiKey: props.apiKey,
        requestBody: {
          limit: 100,
          allowedRegions,
        },
      });
      if (response.ok) {
        setCollections(response.data);
        setInitialized(true);
      } else {
        setInitialized(false);
      }
    } catch (error) {
      Logger.error('Failed to get collections', error as Error, {
        function: 'fetchCollections',
        filename: 'SearchWidget.tsx',
        widgetKey: props.apiKey,
        version: props.version,
      });
    }
  };

  const fetchAnswer = async (query: string, context: any) => {
    // short circuit if hideAnswers is true
    if (hideAnswers) return;

    setAnswering(true);
    setAnswer('');
    setPromptId('');
    try {
      const answerStreamUrlPath = Endpoints[EndpointSource].AnswerStream;

      const readableStream = await kyPost<any>({
        endpoint: `${apiHost}/api/${answerStreamUrlPath}`,
        apiKey: props.apiKey,
        requestBody: { searchQueryId: context?.searchQueryId },
        returnBody: true,
      });

      if (readableStream) {
        let firstStreamedAnswerChunk = true;

        streamAnswer(
          readableStream,
          (runningText) => {
            // break if answering is cancelled
            if (!clearSearch()) {
              setAnswer(runningText);

              const trackData = {
                data: {
                  searchQuery: query,
                  searchQueryId: context.searchQueryId,
                  answer: runningText,
                },
              };

              if (firstStreamedAnswerChunk) {
                eventTracker?.track({
                  ...trackData,
                  name: 'First Streamed Answer Chunk Received',
                  type: EventType.FirstStreamedAnswerChunkReceived,
                });

                firstStreamedAnswerChunk = false;
              }
            }
          },
          (runningText) => {
            if (!clearSearch()) {
              const trackData = {
                data: {
                  searchQuery: query,
                  searchQueryId: context.searchQueryId,
                  answer: runningText,
                },
              };

              if (isMissingResponse(runningText)) {
                eventTracker?.track({
                  ...trackData,
                  name: 'Answer Not Found',
                  type: EventType.AnswerNotFound,
                });
              } else {
                eventTracker?.track({
                  ...trackData,
                  name: 'Answer Found',
                  type: EventType.AnswerFound,
                });
              }
            }

            setAnswering(false);
          },
        );
      } else {
        Logger.error(
          'ReadableStream is null',
          new Error('ReadableStream is null'),
          {
            function: 'fetchAnswer',
            filename: 'SearchWidget.tsx',
            widgetKey: props.apiKey,
            version: props.version,
            searchQueryId: context?.searchQueryId,
          },
        );
      }
    } catch (error) {
      Logger.error('Failed to fetch answer', error as Error, {
        function: 'fetchAnswer',
        filename: 'SearchWidget.tsx',
        widgetKey: props.apiKey,
        version: props.version,
        searchQueryId: context?.searchQueryId,
      });
      setAnswering(false);
    }
  };

  const submitFeedback = async (feedback: Feedback, searchId: any) => {
    try {
      const feedbackPath = Endpoints[EndpointSource].Feedback;
      await kyPost<any>({
        endpoint: `${apiHost}/api/${feedbackPath}`,
        requestBody: {
          response: feedback,
          searchQueryId: searchId,
        },
        apiKey: props.apiKey,
      });

      const event = {
        data: {
          searchQuery: searchQuery(),
          searchQueryId: searchQueryId(),
          promptId: promptId(),
          answer: answer(),
        },
      };

      if (feedback === Feedback.ACCEPT) {
        eventTracker?.track({
          ...event,
          name: 'Answer accepted',
          type: EventType.AnswerAccepted,
        });
      } else if (feedback === Feedback.REJECT) {
        eventTracker?.track({
          ...event,
          name: 'Answer rejected',
          type: EventType.AnswerRejected,
        });
      }
    } catch (error) {
      Logger.error('Failed to submit feedback', error as Error, {
        function: 'submitFeedback',
        filename: 'SearchWidget.tsx',
        widgetKey: props.apiKey,
        version: props.version,
        response: feedback,
        searchQueryId: searchId,
      });
    }
  };

  const submitFeedbackReason = async (reason: string, searchId: any) => {
    try {
      const feedbackPath = Endpoints[EndpointSource].FeedbackReason;
      await kyPost<any>({
        endpoint: `${apiHost}/api/${feedbackPath}`,
        requestBody: {
          reason,
          searchQueryId: searchId,
        },
        apiKey: props.apiKey,
      });

      const event = {
        data: {
          searchQuery: searchQuery(),
          searchQueryId: searchQueryId(),
          promptId: promptId(),
          answer: answer(),
        },
      };
      eventTracker?.track({
        ...event,
        name: 'Feedback Reason Submitted',
        type: EventType.FeedbackReasonSubmitted,
      });
    } catch (error) {
      Logger.error('Failed to submit feedback reason', error as Error, {
        function: 'submitFeedbackReason',
        filename: 'SearchWidget.tsx',
        widgetKey: props.apiKey,
        version: props.version,
        response: reason,
        searchQueryId: searchId,
      });
    }
  };

  onMount(() => {
    if (!searchContainer) return;
    resizeObserver.observe(searchContainer);
  });

  onCleanup(() => {
    if (!searchContainer) return;
    resizeObserver.unobserve(searchContainer);
  });

  createEffect(() => {
    if ((props.settings && props.apiKey) || props.accessToken) {
      fetchCollections();
    }
  });

  const handleSuggestionSearch = (query: string) => {
    eventTracker?.track({
      name: 'Recent Search Clicked',
      type: EventType.RecentSearchClicked,
      data: {
        searchQuery: query,
        searchQueryId: searchQueryId(),
        promptId: promptId(),
        answer: answer(),
      },
    });
    handleSearch(query);
  };

  const handleOnEnter = (query: string) => {
    if (!query || !initialized()) return;

    const searchQ = query.trim();
    if (searchQ.length < 3) return;

    if (props.onAnswer) {
      props.onAnswer(true);
    }

    eventTracker?.track({
      name: 'Typed Question Submitted',
      type: EventType.TypedQuestionSumitted,
      data: {
        searchQuery: searchQ,
        searchQueryId: searchQueryId(),
        promptId: promptId(),
        answer: answer(),
      },
    });
    handleSearch(searchQ);
  };

  const getSearchResultCardUrl = (result: {
    syncSource?: { sourceUrl: string };
    isPublic: boolean;
    url: string;
  }): string => {
    let path = result?.url;

    // check if url is external link and return it
    if (path.startsWith('http')) {
      return getUtmParameters(path, props.widgetType);
    }

    // respect result.isPublic for chromeExtension and non platform environments
    if (
      (isChromeExtension || props.widgetSource !== WIDGET_SOURCE.PLATFORM) &&
      result.isPublic
    ) {
      // replace doc with articles
      path = path.replace('doc', 'articles');

      return getUtmParameters(`${helpCenterUrl()}${path}`, props.widgetType);
    }

    return getUtmParameters(`${apiHost}${path}`, props.widgetType);
  };

  const CategoriesSelection = () => {
    return (
      <div class="flex flex-col gap-2 my-3 w-full">
        <h3
          class="font-bold text-md text-[var(--bfsearch-category-title-color)]"
          part="category-selection-title"
        >
          {props.settings?.categorySelectionTitle || 'Select a category:'}
        </h3>
        <div class="flex flex-row flex-wrap gap-2">
          <For each={collections()}>
            {(collection: any) => {
              return (
                <button
                  part="category-selection-button"
                  class="py-1 px-4 rounded-full border border-[var(--bfsearch-border-color)] bg-[var(--bfsearch-bg-color)] text-[var(--bfsearch-category-text-color)] hover:bg-[var(--bfsearch-bg-hover-color)] transition-colors duration-200 bg-[#F5F5F5]"
                  onClick={() => {
                    eventTracker?.track({
                      name: 'Category Clicked',
                      type: EventType.SearchCategoryClicked,
                      data: {
                        category: collection.name,
                        categoryId: collection.id,
                        searchQuery: searchQuery(),
                      },
                    });
                    setDisplayCategoriesSelection(false);
                    searchArticles(searchQuery(), collection.id);
                  }}
                >
                  {collection.name}
                </button>
              );
            }}
          </For>
        </div>
      </div>
    );
  };

  const onClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setAnswer('');
    setSearchIntent(undefined);
    setSearching(false);
    setAnswering(false);
    setNoResults(false);
    setClearSearch(true);
    setDisplayCategoriesSelection(false);
  };

  createEffect(() => {
    if (props.onAnswer) {
      const isAnswering =
        (!clearSearch() && answering()) || searchResults().length > 0;
      props.onAnswer(isAnswering);
    }
  });

  const isExternalLink = (url: string) => {
    // if the url is not a help center url, it is an external link
    return !url.includes(helpCenterUrl());
  };

  return (
    <div
      ref={searchContainer}
      class={classNames(
        'relative flex w-full bg-cover bg-center flex-col items-center bfsearch-container',
        props.class,
        {
          'text-[var(--bfsearch-text-base-color)]': isSidebar,
        },
      )}
    >
      <div
        class={classNames('w-full', {
          'p-6 text-[var(--bfsearch-text-base-color)] bg-[var(--bfsearch-input-button-bg-color)] border-b border-b-[var(--bfsearch-text-base-color)]':
            isSidebar,
          'mb-4': !isSidebar,
        })}
      >
        <style>{`${styles}`}</style>
        <AutocompleteSearchInput
          id={props.apiKey}
          placeholder={`${
            placeholder ||
            (isMobile()
              ? 'Ask a question...'
              : 'Ask a question or type a search term here...')
          }`}
          getSuggestionsOptions={{
            endpoint: `${apiHost}/api/${Endpoints[EndpointSource].HelpMeta}`,
            requestOptions: {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Brainfish-Widget-Version': VERSION,
                ...(props.apiKey ? { 'api-key': props.apiKey } : {}),
                ...(props.accessToken
                  ? { Authorization: `Bearer ${props.accessToken}` }
                  : {}),
              },
            },
          }}
          question={searchQuery()}
          onClearSearch={onClearSearch}
          onInput={(value) => {
            if (value.trim().length > 0) {
              setClearSearch(false);
            }
            setSearchQuery(value);
          }}
          onSearch={handleOnEnter}
          autoFocus={props.autoFocus}
        />
      </div>
      <div
        class={classNames('w-full', {
          'px-6 mt-6': isSidebar,
          'my-6 pb-6 pt-0 h-[calc(100vh-210px)] lg:h-[calc(100vh-242px)]':
            showResultCards() && isSidebar,
        })}
      >
        <Switch>
          <Match when={displayCategoriesSelection()}>
            <CategoriesSelection />
          </Match>
          <Match when={showSearch()}>
            <div
              class={classNames(
                'relative flex w-full h-full bg-cover bg-center flex-col items-center bfsearch-container text-[var(--bfsearch-text-base-color)]',
                ' overflow-auto scrollbar-thin scrollbar-thumb-rounded scrollbar-track-transparent scrollbar-thumb-gray scrollbar-thumb-gray-hover',
              )}
            >
              <Switch>
                <Match when={showSuggestions()}>
                  <div class="flex flex-col flex-start gap-2 mb-3 w-full">
                    <h3 class="font-bold text-md text-[var(--bfsearch-category-title-color)]">
                      {props.settings?.suggestionsTitle ||
                        'Suggested questions:'}
                    </h3>
                    <div class="flex flex-col gap-1">
                      <For each={suggestions}>
                        {(suggestion: any) => {
                          return (
                            <button
                              class="py-1 text-left w-fit text-[var(--bfsearch-category-text-color)] flex items-center gap-1 px-1 "
                              onClick={() => {
                                setClearSearch(false);
                                setSearchQuery(suggestion);
                                handleSuggestionSearch(suggestion);
                              }}
                            >
                              <SearchIcon
                                class="inline-block mr-1 shrink-0"
                                height="20"
                                width="20"
                              />
                              <span class="text-sm lg:text-base underline underline-offset-4 decoration-2 decoration-[var(--bfsearch-border-color)] hover:text-[var(--bfsearch-highlight-color)] hover:no-underline">
                                {suggestion}
                              </span>
                            </button>
                          );
                        }}
                      </For>
                    </div>
                  </div>
                </Match>
                <Match when={!showSuggestions()}>
                  <Show
                    when={
                      props.settings?.isNextBestActionAboveResults &&
                      showNextBestActionsWithoutAnswer()
                    }
                  >
                    {nextBestActions && apiHost && (
                      <NextBestActions
                        loading={searching() || answering()}
                        hideAnswer={true}
                        actions={nextBestActions}
                        apiHost={apiHost}
                        apiKey={props.apiKey}
                        searchQueryId={searchQueryId()}
                        searchQuery={searchQuery()}
                        promptId={promptId()}
                        answer={''}
                        className="!mt-0"
                        searchIntent={searchIntent()}
                        onAskNewQuestion={onClearSearch}
                      />
                    )}
                  </Show>
                  <Switch>
                    <Match when={showSearching()}>
                      <div class="block w-full p-6 bg-white border border-[var(--bfsearch-border-color)] rounded-md">
                        <div class="relative h-[138px]">
                          <div class="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center bg-base-100 flex-col">
                            <LoadingIndicator class="h-[48px] w-[48px]" />
                            <p class="text-sm text-base-content">
                              {loadingSearchText ||
                                'Searching for related articles...'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Match>
                    <Match when={showAnswering()}>
                      <div class="block w-full p-6 bg-white border border-[var(--bfsearch-border-color)] rounded-md">
                        <div class="relative h-[138px]">
                          <div class="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center bg-base-100 flex-col">
                            <LoadingIndicator class="h-[48px] w-[48px]" />
                            <p class="text-sm text-base-content">
                              {loadingAnswerText || 'Crafting an answer...'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Match>
                  </Switch>

                  <Show when={showAnswer()}>
                    <div class="w-full">
                      <AnswerCard
                        answer={answer()}
                        answering={answering()}
                        identifyingIntent={identifyingIntent()}
                        hideFeedback={hideFeedback}
                        showDisclaimer={showDisclaimer}
                        disclaimer={disclaimer}
                        onFeedback={(feedback) => {
                          submitFeedback(feedback, searchQueryId());
                        }}
                        onFeedbackReasonSubmit={(reason) => {
                          submitFeedbackReason(reason, searchQueryId());
                        }}
                        nextBestActions={nextBestActions}
                        apiHost={apiHost}
                        apiKey={props.apiKey}
                        searchQueryId={searchQueryId()}
                        searchQuery={searchQuery()}
                        promptId={promptId()}
                        eventTracker={eventTracker}
                        answerNotFoundMessage={answerNotFoundMessage}
                        searchIntent={searchIntent()}
                        onAskNewQuestion={onClearSearch}
                        generatingAnswerText={generatingAnswerText}
                        suggestedAnswerText={suggestedAnswerText}
                        widgetType={props.widgetType}
                      />
                    </div>
                  </Show>
                  <Show when={showResultCards()}>
                    <div class="w-full my-4">
                      <SearchResultCards>
                        <For each={searchResults().slice(0, limit)}>
                          {(result: SearchResult) => {
                            return (
                              <SearchResultCard
                                title={result?.title}
                                description={result.chunk}
                                category={result?.collection?.name}
                                isPublic={result.isPublic}
                                onClick={() =>
                                  eventTracker?.track({
                                    name: 'Search Result Clicked',
                                    type: EventType.SearchResultClicked,
                                    data: {
                                      searchQuery: searchQuery(),
                                      searchQueryId: searchQueryId(),
                                      promptId: promptId(),
                                      answer: answer(),
                                    },
                                  })
                                }
                                isExternalLink={isExternalLink(result.url)}
                                openInModal={props.settings?.openInModal}
                                url={getSearchResultCardUrl(result)}
                              />
                            );
                          }}
                        </For>
                      </SearchResultCards>
                    </div>
                  </Show>
                  <Show when={showNoResults()}>
                    <div class="block w-full p-6 bg-white border border-[var(--bfsearch-border-color)] rounded-md">
                      <div class="relative">
                        <div class="flex h-full w-full items-start justify-center bg-base-100 flex-col">
                          <p class="text-[16px] text-base-content">
                            {props.settings?.answerNotFoundMessage ||
                              DEFAULT_NO_ANSWER_RESPONSE}
                          </p>
                          <NextBestActions
                            loading={searching() || answering()}
                            className="w-full"
                            actions={nextBestActions || []}
                            apiHost={apiHost}
                            apiKey={props.apiKey}
                            searchQueryId={searchQueryId()}
                            searchQuery={searchQuery()}
                            promptId={promptId()}
                            answer={answer()}
                            searchIntent={searchIntent()}
                            onAskNewQuestion={onClearSearch}
                          />
                        </div>
                      </div>
                    </div>
                  </Show>
                  <Show
                    when={
                      !props.settings?.isNextBestActionAboveResults &&
                      showNextBestActionsWithoutAnswer()
                    }
                  >
                    {nextBestActions && apiHost && (
                      <NextBestActions
                        loading={searching() || answering()}
                        hideAnswer={true}
                        actions={nextBestActions}
                        apiHost={apiHost}
                        apiKey={props.apiKey}
                        searchQueryId={searchQueryId()}
                        searchQuery={searchQuery()}
                        promptId={promptId()}
                        answer={answer()}
                        searchIntent={searchIntent()}
                        onAskNewQuestion={onClearSearch}
                      />
                    )}
                  </Show>
                </Match>
              </Switch>
            </div>
          </Match>
        </Switch>
      </div>
    </div>
  );
};

export default Search;
